// React and MobX
import React, { useEffect, useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, Card, CardActionArea, CardContent, Drawer, Grid, Typography, useMediaQuery, useTheme } from "@material-ui/core";

// Custom Components
import { ChoicesArea } from "./ChoicesArea";
import { Stores, StoresContext } from "Custom/Stores";

// Custom Styles
import { ChoicesPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/ChoicesPageStyling";
import { ChoicesProductGroupStyles } from "Custom/StylesAppSpecific/DashboardSubViews/ChoicesProductGroupStyling";

// Custom ViewModels
import { ChoicesSummaryViewModel } from "Custom/ViewModels/DashboardSubViewModels/ChoicesSummaryViewModel";

// Custom Utils
import { Format } from "Custom/Utils";

// Custom Content/Icons
import { ReactComponent as EditIcon } from "Custom/Content/Images/editGreen.svg";

interface IProps {
    propertyId: string;
    canEditChoices?: boolean;
    viewModelSummary: ChoicesSummaryViewModel;
    stores: Stores;
}

export const ChoicesSummary: React.FC<IProps> = props => {
    // #region Code Behind
    const propertyId = props.propertyId;
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const choicesPageStyles = ChoicesPageStyles();
    const choicesProductGroupStyles = ChoicesProductGroupStyles();
    const portalThemeStore = props.stores.domain.PortalThemeStore;

    const viewModel = props.viewModelSummary;
    // const [viewModel] = useState(() => new ChoicesSummaryViewModel(propertyId));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedArea, setSelectedArea] = useState<string | null>(null);

    const onChoicesAreaClick = (areaId: string) => {
        if (isDesktop) {
            viewModel.navigateToChoicesArea(propertyId, areaId); // Navigate for desktop
        } else {
            setSelectedArea(areaId);
            setDrawerOpen(true);
        }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedArea(null);
    };

    // Pre-process bespoke areas to avoid calling addAreaId during render
    useEffect(() => {
        if (viewModel.productBespoke) {
            viewModel.productBespoke.forEach(bespoke => {
                if (!viewModel.areaId.includes(bespoke.areaId)) {
                    viewModel.addAreaId(bespoke.areaId);
                }
            });
        }
    }, [viewModel.productBespoke]);
    // #endregion Code Behind

    const renderTitleRow = () => (
        <Box
            className={choicesPageStyles.choicesAreasMainHeadingBlock}
            style={{
                ...(portalThemeStore.primaryColour ? { backgroundColor: portalThemeStore.primaryColour } : {}),
            }}
        >
            <Typography component="h3" className={choicesPageStyles.choicesAreasMainSummaryTitle}>
                Choices selected so far
            </Typography>
            <div 
                className={choicesPageStyles.choicesAreasSummarySubtitle}
                style={{
                    ...(portalThemeStore.secondaryColour ? { backgroundColor: portalThemeStore.secondaryColour } : {}),
                }}
            >
                <Grid className={choicesPageStyles.choicesAreasSummarySubTotal} item>
                    <div>Order value:</div>
                    <div>{viewModel.orderValue ? "£" + viewModel.orderValue : "-"}</div>
                </Grid>
                <Grid className={choicesPageStyles.choicesAreasSummarySubTotal} item>
                    <div>Outstanding balance:</div>
                    <div>{viewModel.outstandingBalanceTotal ? "£" + viewModel.outstandingBalanceTotal : "-"}</div>
                </Grid>
            </div>
        </Box>
    );

    const renderViewEditChoicesButton = () => {
        if (props.canEditChoices !== undefined && props.canEditChoices === false) {
            return <span style={{ textDecoration: "underline", color: "#556ed1" }}>View choices</span>;
        } else {
            return <EditIcon className={choicesPageStyles.editIcon} style={{ color: portalThemeStore.secondaryColour || "#449B23" }} />;
        }
    };

    const renderChoicesSummary = () => (
        <Box className={choicesPageStyles.choicesSummaryCardRow}>
            {viewModel.areas.map((choicesArea, index) => (
                <Card className={choicesPageStyles.choicesAreaSummary} key={index}>
                    <CardContent>
                        <Box className={choicesPageStyles.choicesAreaSummaryTitleContainer}>
                            <Typography className={choicesPageStyles.choicesAreaSummaryTitle}>{choicesArea.model.areaName}</Typography>
                            <CardActionArea
                                style={{
                                    background: props.canEditChoices !== undefined && props.canEditChoices === false ? "none" : portalThemeStore.secondaryColour || "#449B23",
                                    width: props.canEditChoices !== undefined && props.canEditChoices === false ? "85px" : "30px",
                                    padding: 0,
                                    overflow: "hidden",
                                }}
                                onClick={() => onChoicesAreaClick(choicesArea.model.areaId)}
                            >
                                {renderViewEditChoicesButton()}
                            </CardActionArea>
                        </Box>

                        <Box className={choicesPageStyles.choicesAreaSummaryContent}>
                            {/* Options */}
                            <Box className={choicesPageStyles.choicesAreaSummaryOptionsContainer}>
                                <Typography className={choicesPageStyles.choicesAreaSummarySubTitle}>Options you selected:</Typography>

                                {choicesArea.productGroupsSummary.map((productGroup, index) => (
                                    <Box key={index}>
                                        {productGroup.options.map((product, prodIndex) => (
                                            <Box className={choicesPageStyles.productSummaryContainer} key={`${prodIndex}-${index}`}>
                                                <Box className={choicesPageStyles.smallText}>{product.productTitle}</Box>
                                                {product.productQuantity && (
                                                    <Box>
                                                        <span className={choicesProductGroupStyles.productTitle}>Quantity:</span> {product.productQuantity}
                                                    </Box>
                                                )}
                                                <Box>{product.productQuantity != null && product.productQuantity > 1 ? product.getPriceOnAddQuantity() : product.cost}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>

                            {/* Extras */}
                            <Box className={choicesPageStyles.choicesAreaSummaryExtrasContainer}>
                                <Typography className={choicesPageStyles.choicesAreaSummarySubTitle}>Extras, you selected:</Typography>

                                {choicesArea.productGroupsSummary.map((productGroup, index) => (
                                    <Box key={index}>
                                        {productGroup.extras.map((product, prodIndex) => (
                                            <Box className={choicesPageStyles.productSummaryContainer} key={`${prodIndex}-${index}`}>
                                                <Box className={choicesPageStyles.smallText}>{product.productTitle}</Box>
                                                {product.productQuantity && (
                                                    <Box>
                                                        <span className={choicesProductGroupStyles.productTitle}>Quantity:</span> {product.productQuantity}
                                                    </Box>
                                                )}
                                                <Box>{product.productQuantity != null && product.productQuantity > 1 ? product.getPriceOnAddQuantity() : product.cost}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Bespoke */}
                        {viewModel.productBespoke
                            .filter(bespoke => bespoke.areaId === choicesArea.model.areaId)
                            .map((bespoke, bespokeIndex) => (
                                <Box key={bespokeIndex} className={choicesPageStyles.choicesAreaSummaryExtrasContainer}>
                                    {!viewModel.areaId.includes(bespoke.areaId) && <Typography className={choicesPageStyles.choicesAreaSummarySubTitle}>Bespoke:</Typography>}
                                    <Box className={choicesPageStyles.productSummaryContainer}>
                                        <Box>{`${bespoke.name} - ${bespoke.notes}`}</Box>
                                        <Box>{bespoke.poa ? "POA" : bespoke.isPaid ? "Paid" : `£${Format.currencyFormat(bespoke.cost)}`}</Box>
                                    </Box>
                                </Box>
                            ))}
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    return useObserver(() => (
        <Box>
            {viewModel.hasSelectedChoices && renderTitleRow()}
            <Box className={choicesPageStyles.choicesAreasSummary}>{renderChoicesSummary()}</Box>

            {/* Drawer for mobile */}
            <Drawer anchor="bottom" open={drawerOpen} onClose={closeDrawer} className="choicesPageMobileDrawer">
                {selectedArea && <ChoicesArea propertyId={propertyId} areaId={selectedArea} onClose={closeDrawer} stores={props.stores} />}
            </Drawer>
        </Box>
    ));
};
